export const SOUND_OFF_KEY = 'bb-chime-sound-off-v1';
export const SOUND_INVITE_KEY = 'bb-chime-invite-seen-v1';

export function shouldAttemptSound({ enabled = true, source, autoplay, off, visible, competing, editor }) {
  return Boolean(enabled && source && autoplay && !off && visible && !competing && !editor);
}

// Media behavior is separate from the DOM so interruption and preference rules are testable.
export class ChimeSoundSession {
  constructor({ audio, read = () => null, write = () => {}, visible = () => true, competing = () => false,
    pauseCompeting = () => {}, changed = () => {} }) {
    this.audio = audio;
    this.read = read;
    this.write = write;
    this.visible = visible;
    this.competing = competing;
    this.pauseCompeting = pauseCompeting;
    this.changed = changed;
    this.volume = 1;
    this.off = this.read(SOUND_OFF_KEY) === 'true';
    this.state = this.off ? 'off' : 'ready';
    this.autoplayAttempted = false;
    this.wanted = false;
    this.attempt = 0;
    this.destroyed = false;
    this.listeners = [];
    this.audio.volume = this.volume;
    this.listen('playing', () => {
      if (!this.wanted || !this.visible()) {
        if (!this.visible()) this.suspend('background');
        else this.audio.pause();
        return;
      }
      if (this.competing()) { this.suspend('other_media'); return; }
      this.setState('playing');
    });
    this.listen('pause', () => {
      if (this.state === 'playing' || this.state === 'starting') {
        this.wanted = false;
        this.attempt += 1;
        this.setState('paused');
      }
    });
    this.listen('ended', () => {
      this.wanted = false;
      this.attempt += 1;
      this.setState('ended');
    });
    this.listen('error', () => {
      this.wanted = false;
      this.attempt += 1;
      this.setState('error');
    });
  }
  listen(name, callback) {
    this.audio.addEventListener(name, callback);
    this.listeners.push([name, callback]);
  }
  setState(state) {
    if (this.destroyed) return;
    this.state = state;
    this.changed(this);
  }
  autoplay(options) {
    if (this.autoplayAttempted || this.destroyed || !shouldAttemptSound({ ...options, off: this.off, visible: this.visible(), competing: this.competing() })) return;
    this.autoplayAttempted = true;
    return this.start();
  }
  async start({ explicit = false } = {}) {
    if (this.destroyed || this.wanted) return;
    if (!this.visible()) { this.setState('background'); return; }
    if (!explicit && this.off) { this.setState('off'); return; }
    if (!explicit && this.competing()) { this.setState('other_media'); return; }
    if (explicit) {
      // This branch is called only by the actual sound control button.
      this.off = false;
      this.autoplayAttempted = true;
      this.write(SOUND_OFF_KEY, 'false');
      this.pauseCompeting();
    }
    if (this.audio.error) this.audio.load();
    const currentAttempt = ++this.attempt;
    this.wanted = true;
    this.setState('starting');
    try {
      await this.audio.play();
      if (this.destroyed || currentAttempt !== this.attempt || !this.wanted) {
        if (this.destroyed || !this.wanted) this.audio.pause();
        return;
      }
      if (!this.visible()) { this.suspend('background'); return; }
      if (this.competing()) { this.suspend('other_media'); return; }
      // A resolved play() promise is not used to announce sound before actual playback.
      if (!this.audio.paused) this.setState('playing');
    } catch (error) {
      if (this.destroyed || currentAttempt !== this.attempt) return;
      this.wanted = false;
      this.setState(error?.name === 'NotAllowedError' ? 'blocked' : 'error');
    }
  }
  turnOff({ persist = true } = {}) {
    this.off = true;
    this.wanted = false;
    this.attempt += 1;
    if (persist) this.write(SOUND_OFF_KEY, 'true');
    this.setState('off');
    this.audio.pause();
  }
  suspend(reason = 'paused') {
    if (this.destroyed) return;
    this.wanted = false;
    this.attempt += 1;
    this.setState(reason);
    this.audio.pause();
  }
  destroy() {
    this.wanted = false;
    this.attempt += 1;
    this.destroyed = true;
    this.audio.pause();
    this.listeners.forEach(([name, callback]) => this.audio.removeEventListener(name, callback));
  }
}

export class ChimeInvitation {
  constructor({ dialog, read = () => null, write = () => {}, available = () => true, active = () => null }) {
    this.dialog = dialog;
    this.write = write;
    this.available = available;
    this.active = active;
    this.seen = read(SOUND_INVITE_KEY) === '1';
    this.trigger = null;
    this.destroyed = false;
  }
  update(session) {
    if (this.destroyed) return;
    if (['playing', 'off', 'background', 'other_media'].includes(session.state)) this.close();
    if (session.state !== 'blocked' || session.off || this.seen || !this.available() || typeof this.dialog?.showModal !== 'function') return;
    this.trigger = this.active();
    try { this.dialog.showModal(); } catch { return; }
    this.seen = true;
    this.write(SOUND_INVITE_KEY, '1');
  }
  close() { if (this.dialog?.open) this.dialog.close(); }
  restoreFocus() { if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true }); }
  destroy() { this.destroyed = true; this.close(); }
}

if (typeof document !== 'undefined') {
  const controllers = new Map();
  const localRead = key => { try { return window.localStorage.getItem(key); } catch { return null; } };
  const localWrite = (key, value) => { try { window.localStorage.setItem(key, value); } catch { /* Playback remains usable when storage is unavailable. */ } };
  const audible = media => !media.paused && !media.ended && !media.muted && media.volume > 0;
  function initialize(root) {
    if (controllers.has(root)) return;
    const audio = root.querySelector('[data-bb-sound-audio]');
    if (!audio?.getAttribute('src')) return;
    const inline = root.querySelector('[data-bb-sound-inline]');
    const controls = root.querySelector('[data-bb-sound-controls]');
    const mini = root.querySelector('[data-bb-sound-mini]');
    const buttons = [...root.querySelectorAll('[data-bb-sound-toggle]')];
    const dialog = root.querySelector('[data-bb-sound-invite]');
    const invitePlay = root.querySelector('[data-bb-sound-listen]');
    const inviteStatus = root.querySelector('[data-bb-sound-invite-status]');
    const status = root.querySelector('[data-bb-sound-status]');
    let labels;
    try { labels = JSON.parse(root.querySelector('[data-bb-sound-text]').textContent); } catch { return; }
    let inlineVisible = true;
    let isEditor = Boolean(window.Shopify?.designMode);
    const otherMedia = () => [...document.querySelectorAll('video,audio')].filter(item => item !== audio);
    const invitation = new ChimeInvitation({
      dialog,
      read: key => { try { return sessionStorage.getItem(key); } catch { return null; } },
      write: (key, value) => { try { sessionStorage.setItem(key, value); } catch {} },
      active: () => document.activeElement,
      available: () => !isEditor && !document.hidden && !otherMedia().some(audible) && ![...document.querySelectorAll('dialog[open]')].some(item => item !== dialog)
    });
    function render(session) {
      const playing = session.state === 'playing' && !audio.paused;
      const starting = session.state === 'starting';
      const buttonText = playing ? labels.off : starting ? labels.cancel : session.state === 'error' ? labels.retry : labels.play;
      root.dataset.state = session.state;
      controls.setAttribute('aria-busy', String(starting));
      buttons.forEach(button => {
        button.textContent = buttonText;
        button.setAttribute('aria-pressed', String(playing));
        button.setAttribute('aria-label', buttonText);
      });
      const message = session.state === 'off' ? labels.off_status : labels[session.state];
      if (status.textContent !== message) status.textContent = message || labels.ready;
      if (invitePlay) {
        invitePlay.disabled = starting;
        invitePlay.textContent = starting ? labels.starting : session.state === 'error' ? labels.retry : labels.listen;
      }
      if (inviteStatus) inviteStatus.textContent = starting || session.state === 'error' ? message : '';
      invitation.update(session);
      // Off stays out of the way; the inline player remains available to turn sound back on.
      mini.hidden = inlineVisible || isEditor || document.hidden || Boolean(dialog?.open);
    }
    const session = new ChimeSoundSession({
      audio, read: localRead, write: localWrite,
      visible: () => !document.hidden,
      competing: () => otherMedia().some(audible),
      pauseCompeting: () => otherMedia().filter(audible).forEach(media => media.pause()),
      changed: state => { render(state); try { window.BBAnalytics?.soundState(root,state); } catch {} }
    });
    const listeners = [];
    const on = (element, name, callback, capture = false) => {
      element.addEventListener(name, callback, capture);
      listeners.push(() => element.removeEventListener(name, callback, capture));
    };
    buttons.forEach(button => on(button, 'click', () => {
      if (session.wanted || !audio.paused) session.turnOff();
      else session.start({ explicit: true });
    }));
    if (invitePlay) on(invitePlay, 'click', () => session.start({ explicit: true }));
    const dismiss = () => { session.turnOff(); invitation.close(); };
    root.querySelectorAll('[data-bb-sound-dismiss]').forEach(button => on(button, 'click', dismiss));
    if (dialog) {
      on(dialog, 'cancel', event => { event.preventDefault(); dismiss(); });
      on(dialog, 'close', () => { invitation.restoreFocus(); render(session); });
    }
    on(document, 'close', () => invitation.update(session), true);
    let languageReady = false;
    const autoplay = () => {
      if (!languageReady) return;
      session.autoplay({ source: audio.getAttribute('src'), autoplay: root.dataset.autoplayAttempt === 'true', editor: isEditor });
    };
    on(document, 'visibilitychange', () => {
      if (document.hidden && (session.wanted || !audio.paused)) session.suspend('background');
      if (!document.hidden) autoplay();
      render(session); // Becoming visible only updates controls, never restarts audio.
    });
    on(window, 'pagehide', () => session.suspend('background'));
    on(window, 'pageshow', () => render(session));
    on(window, 'storage', event => {
      if (event.key === SOUND_OFF_KEY && event.newValue === 'true') session.turnOff({ persist: false });
    });
    const competingStarted = event => {
      const media = event.target;
      if (media !== audio && ['AUDIO', 'VIDEO'].includes(media?.tagName) && audible(media) && (session.wanted || !audio.paused)) session.suspend('other_media');
    };
    on(document, 'play', competingStarted, true);
    on(document, 'volumechange', competingStarted, true);
    // Future iframe players must dispatch this event when their real media starts.
    on(document, 'bb:media-play', event => {
      if (event.detail?.source !== 'bb-sound' && (session.wanted || !audio.paused)) session.suspend('other_media');
    });
    let intersection;
    if ('IntersectionObserver' in window) {
      intersection = new IntersectionObserver(entries => {
        inlineVisible = entries[0].isIntersecting;
        render(session);
      }, { threshold: 0.1 });
      intersection.observe(inline);
    } else {
      // Keep an accessible stop control when autoplay can happen without this API.
      inlineVisible = false;
    }
    const sticky = document.querySelector('[data-bb-sticky]');
    function positionMini() {
      const height = sticky && !sticky.hidden ? sticky.getBoundingClientRect().height : 0;
      if (height > 0) root.style.setProperty('--bb-sound-sticky-height', `${Math.ceil(height)}px`);
    }
    let resize;
    if (sticky && 'ResizeObserver' in window) {
      resize = new ResizeObserver(positionMini);
      resize.observe(sticky);
    }
    on(window, 'resize', positionMini);
    on(window, 'orientationchange', positionMini);
    positionMini();
    audio.controls = false;
    audio.hidden = true;
    controls.hidden = false;
    status.hidden = false;
    render(session);
    controllers.set(root, () => {
      session.destroy();
      invitation.destroy();
      listeners.forEach(remove => remove());
      intersection?.disconnect();
      resize?.disconnect();
      controllers.delete(root);
    });
    Promise.resolve(window.bbLocaleReady).then(result => {
      if (session.destroyed || result?.redirecting) return;
      languageReady = true;
      autoplay();
    });
  }
  const initializeWithin = scope => {
    if (scope.matches?.('[data-bb-sound]')) initialize(scope);
    scope.querySelectorAll('[data-bb-sound]').forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of controllers) if (event.target.contains(root)) dispose();
  });
}
